import { useTaskSummary } from "./task-pages";
import { tr } from "./localization";
import { useEffect, useState, type ReactNode } from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Plus,
  Search,
  Bell,
  Moon,
  Sun,
  Inbox,
  Send,
  Layers,
  Users,
  UserRound,
  SlidersHorizontal,
  LogOut,
  Clock,
  AlertCircle,
  Info,
  MessageSquare,
  Columns3,
  CheckCircle,
  Eye,
  Circle,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "./api";
import type { Board, TaskList, User, Notification } from "./types";
import { notificationTime } from "./utils";
import {
  type Ctx,
  Context,
  useApp,
  err,
  Avatar,
  Button,
  Modal,
  Loading,
  Empty,
  ErrorState,
} from "./shared";
import { CreateTask } from "./create-task";
import { TaskLink, TaskDrawer } from "./task-drawer";
import { AuthPage } from "./auth";
import { FirstRun, OnboardingTour } from "./onboarding";
import { lazy, Suspense } from "react";
const MyTasks = lazy(() =>
  import("./my-tasks").then((m) => ({ default: m.MyTasks })),
);
const Boards = lazy(() =>
  import("./board").then((m) => ({ default: m.Boards })),
);
const BoardPage = lazy(() =>
  import("./board").then((m) => ({ default: m.BoardPage })),
);
const BoardSettings = lazy(() =>
  import("./board-settings").then((m) => ({ default: m.BoardSettings })),
);
const Profile = lazy(() =>
  import("./profile").then((m) => ({ default: m.Profile })),
);
const Admin = lazy(() => import("./admin").then((m) => ({ default: m.Admin })));
const Statistics = lazy(() =>
  import("./statistics").then((m) => ({ default: m.Statistics })),
);

function useNewVersionNotice() {
  useEffect(() => {
    if (import.meta.env.DEV) return;
    let shown = false;
    const check = async () => {
      try {
        const r = await fetch("/version.json", { cache: "no-store" });
        if (!r.ok) return;
        const { stamp } = (await r.json()) as { stamp?: string };
        if (stamp && stamp !== __BUILD_STAMP__ && !shown) {
          shown = true;
          toast(tr("Вышла новая версия приложения"), {
            duration: Infinity,
            action: { label: tr("Обновить"), onClick: () => location.reload() },
          });
        }
      } catch {
        /* нет сети — проверим позже */
      }
    };
    check();
    const timer = window.setInterval(check, 60_000);
    window.addEventListener("focus", check);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", check);
    };
  }, []);
}

export default function App() {
  useNewVersionNotice();
  const qc = useQueryClient(),
    location = useLocation(),
    navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const meQ = useQuery({
    queryKey: ["me"],
    queryFn: () => api<User>("/me"),
    retry: false,
  });
  const usersQ = useQuery({
    queryKey: ["users"],
    queryFn: () => api<User[]>("/users"),
    enabled: !!meQ.data,
  });
  const boardsQ = useQuery({
    queryKey: ["boards"],
    queryFn: () => api<Board[]>("/boards"),
    enabled: !!meQ.data,
  });
  const [create, setCreate] = useState<{
      prefill?: string;
      boardId?: string;
      columnId?: string;
    } | null>(null),
    [palette, setPalette] = useState(false),
    [notifications, setNotifications] = useState(false);
  useEffect(() => {
    const handler = () =>
      setTheme(localStorage.getItem("ims-theme") || "system");
    window.addEventListener("ims-theme-changed", handler);
    return () => window.removeEventListener("ims-theme-changed", handler);
  }, []);
  const [liveEpoch, setLiveEpoch] = useState(0);
  const [theme, setTheme] = useState(
      () => localStorage.getItem("ims-theme") || "system",
    ),
    [online, setOnline] = useState(navigator.onLine);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["me"] });
    qc.invalidateQueries({ queryKey: ["members"] });
    qc.invalidateQueries({ queryKey: ["departments"] });
    qc.invalidateQueries({ queryKey: ["users"] });
    qc.invalidateQueries({ queryKey: ["invitations"] });
    qc.invalidateQueries({ queryKey: ["task-summary"] });
    qc.invalidateQueries({ queryKey: ["tasks"] });
    qc.invalidateQueries({ queryKey: ["task"] });
    qc.invalidateQueries({ queryKey: ["boards"] });
    qc.invalidateQueries({ queryKey: ["notifications"] });
    qc.invalidateQueries({ queryKey: ["search"] });
    qc.invalidateQueries({ queryKey: ["columns"] });
    qc.invalidateQueries({ queryKey: ["statistics"] });
  };
  const openTask = (id: string, replace = false) => {
    const p = new URLSearchParams(params);
    p.set("task", id);
    setParams(p, { replace });
  };
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("ims-theme", theme);
  }, [theme]);
  useEffect(() => {
    const on = () => setOnline(navigator.onLine);
    window.addEventListener("online", on);
    window.addEventListener("offline", on);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", on);
    };
  }, []);
  useEffect(() => {
    if (!meQ.data) return;
    const es = new EventSource("/api/v1/events");
    es.addEventListener("invalidate", refresh);
    es.addEventListener("session_expired", async () => {
      es.close();
      try {
        await api("/me");
        qc.invalidateQueries({ queryKey: ["me"] });
        setLiveEpoch((v) => v + 1);
      } catch {
        qc.clear();
        qc.setQueryData(["me"], null);
      }
    });
    return () => es.close();
  }, [meQ.data?.id, liveEpoch]);
  useEffect(() => {
    if (!meQ.data) return;
    const fn = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((v) => !v);
        return;
      }
      if (
        e.target instanceof HTMLElement &&
        (e.target.closest("input,textarea,[contenteditable=true],select") ||
          e.target.closest("[role=dialog]"))
      )
        return;
      if (e.key === "c" || e.key === "C") {
        e.preventDefault();
        setCreate({});
      }
      if (e.key === "/") {
        e.preventDefault();
        setPalette(true);
      }
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [meQ.data?.id]);
  if (["/login", "/invite", "/reset", "/forgot"].includes(location.pathname))
    return (
      <>
        <AuthPage
          onSuccess={async () => {
            const user = await api<User>("/me");
            qc.clear();
            qc.setQueryData(["me"], user);
            const from = (location.state as { from?: string } | null)?.from;
            navigate(
              from?.startsWith("/") && !from.startsWith("//") ? from : "/",
              { replace: true },
            );
          }}
        />
        <Toaster richColors position="bottom-right" />
      </>
    );
  if (meQ.isPending) return <Loading />;
  if (!meQ.data) {
    if (meQ.error instanceof ApiError && meQ.error.status !== 401)
      return <ErrorState error={meQ.error} retry={() => meQ.refetch()} />;
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search + location.hash }}
      />
    );
  }
  const failedContext = [usersQ, boardsQ].find((query) => query.isError);
  if (failedContext)
    return (
      <ErrorState
        error={failedContext.error}
        retry={() => failedContext.refetch()}
      />
    );
  if (usersQ.isPending || boardsQ.isPending) return <Loading />;
  const ctx: Ctx = {
    me: meQ.data,
    users: usersQ.data || [],
    boards: boardsQ.data || [],
    openTask,
    create: (prefill, boardId, columnId) =>
      setCreate({ prefill, boardId, columnId }),
    refresh,
  };
  return (
    <Context.Provider value={ctx}>
      <div className="app-shell">
        <Sidebar />
        <div className="app-main">
          <header className="topbar">
            <button className="search-launch" onClick={() => setPalette(true)}>
              <Search size={16} />
              <span>{tr("Поиск задачи или команда…")}</span>
              <kbd>Ctrl K</kbd>
            </button>
            <div className="top-actions">
              <NotificationBell open={() => setNotifications(true)} />
              <Button
                className="icon"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                aria-label={tr("Сменить тему")}
              >
                {theme === "dark" ? <Sun size={19} /> : <Moon size={19} />}
              </Button>
            </div>
          </header>
          {!online && (
            <div className="offline" role="status">
              {tr(
                "Нет сети. Изменения временно недоступны; введённый текст сохранён.",
              )}
            </div>
          )}
          <main id="main">
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route path="/" element={<MyTasks />} />
                <Route path="/delegated" element={<MyTasks delegated />} />
                <Route path="/boards" element={<Boards />} />
                <Route path="/boards/:boardId" element={<BoardPage />} />
                <Route
                  path="/boards/:boardId/settings"
                  element={<BoardSettings />}
                />
                <Route path="/profile" element={<Profile />} />
                <Route path="/first-run" element={<FirstRun />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/stats" element={<Statistics />} />
                <Route path="/tasks/:taskId" element={<TaskLink />} />
                <Route
                  path="*"
                  element={<Empty text={tr("Страница не найдена")} />}
                />
              </Routes>
            </Suspense>
          </main>
        </div>
        <nav className="bottom-nav" aria-label={tr("Основная навигация")}>
          <Link to="/">
            <Inbox size={20} />
            {tr("Мои задачи")}
          </Link>
          <Link to="/boards">
            <Layers size={20} />
            {tr("Доски")}
          </Link>
          <button onClick={() => setPalette(true)}>
            <Search size={20} />
            {tr("Поиск")}
          </button>
          <Link to="/profile">
            <UserRound size={20} />
            {tr("Профиль")}
          </Link>
        </nav>
        <button
          className="fab"
          aria-label={tr("Новая задача")}
          onClick={() => setCreate({})}
        >
          <Plus />
        </button>
      </div>
      <OnboardingTour />
      <CreateTask
        open={create !== null}
        config={create || {}}
        close={() => setCreate(null)}
      />
      <Palette open={palette} close={() => setPalette(false)} />
      <Notifications
        open={notifications}
        close={() => setNotifications(false)}
      />
      {params.get("task") && (
        <TaskDrawer
          id={params.get("task")!}
          close={() => {
            const p = new URLSearchParams(params);
            p.delete("task");
            setParams(p, { replace: true });
          }}
        />
      )}
      {!params.get("task") && (
        <Toaster richColors position="bottom-right" duration={7000} />
      )}
    </Context.Provider>
  );
}

function Sidebar() {
  const { me, boards, create } = useApp(),
    location = useLocation(),
    qc = useQueryClient(),
    navigate = useNavigate(),
    { t } = useTranslation();
  const summary = useTaskSummary();
  return (
    <aside className="sidebar">
      <Link to="/" aria-label="IMS Tasks">
        <img src="/IMS-logo.svg" alt="IMS" className="logo" />
      </Link>
      <Button className="primary new-task" onClick={() => create()}>
        <Plus size={17} />
        {t(tr("Новая задача"))}
        <kbd>C</kbd>
      </Button>
      <nav aria-label={tr("Навигация")}>
        <Link className={location.pathname === "/" ? "active" : ""} to="/">
          <Inbox size={17} />
          {t(tr("Мои задачи"))}
          <span className="badge neutral">{summary.data?.mine || 0}</span>
        </Link>
        <Link
          className={location.pathname === "/delegated" ? "active" : ""}
          to="/delegated"
        >
          <Send size={17} />
          {t(tr("Я поставил"))}
          {!!summary.data?.delegated_review && (
            <span className="badge">{summary.data?.delegated_review}</span>
          )}
        </Link>
        <div className="nav-section">
          {tr("Доски")}{" "}
          <Link
            to={me.role === "employee" ? "/boards" : "/boards?create=1"}
            aria-label={
              me.role === "employee" ? tr("Список досок") : tr("Создать доску")
            }
          >
            <Plus size={16} />
          </Link>
        </div>
        {boards
          .filter((b) => !b.is_archived)
          .map((b, i) => (
            <Link
              key={b.id}
              className={
                location.pathname === `/boards/${b.id}` ? "active" : ""
              }
              to={`/boards/${b.id}`}
            >
              <span className={"board-dot color-" + (i % 4)} />
              <span className="sidebar-board-name">{b.name}</span>
              {b.role === "guest" && <small>{tr("гость")}</small>}
              {!!summary.data?.mine_by_board?.[b.id] && (
                <span
                  className="badge neutral"
                  title={tr("Мои незавершённые задачи")}
                  aria-label={`${tr("Мои незавершённые задачи")}: ${summary.data.mine_by_board[b.id]}`}
                >
                  {summary.data.mine_by_board[b.id]}
                </span>
              )}
            </Link>
          ))}
        {me.role === "admin" && boards.some((b) => b.is_archived) && (
          <Link
            className={
              location.pathname === "/boards" &&
              location.search.includes("archived=1")
                ? "active"
                : ""
            }
            to="/boards?archived=1"
          >
            {tr("Архив досок")}{" "}
            <span className="badge">
              {boards.filter((b) => b.is_archived).length}
            </span>
          </Link>
        )}
        {me.role === "admin" && (
          <>
            <div className="nav-section">{tr("Администрирование")}</div>
            <Link
              className={location.pathname === "/admin" ? "active" : ""}
              to="/admin"
            >
              <Users size={17} />
              {t(tr("Сотрудники"))}
            </Link>
          </>
        )}
        {["admin", "manager"].includes(me.role || "") && (
          <Link
            className={location.pathname === "/stats" ? "active" : ""}
            to="/stats"
          >
            <Columns3 size={17} />
            {tr("Статистика")}
          </Link>
        )}
      </nav>
      <div className="sidebar-foot">
        <Link to="/profile">
          <Avatar user={me} />
          <span>
            {me.name}
            <small>
              {me.role === "admin"
                ? tr("Администратор") +
                  (boards.find((b) => b.department_id === me.department_id)
                    ?.name
                    ? " · " +
                      boards.find((b) => b.department_id === me.department_id)!
                        .name
                    : "")
                : me.role === "manager"
                  ? tr("Руководитель")
                  : tr("Сотрудник")}
            </small>
          </span>
        </Link>
        <Button
          className="icon"
          aria-label={tr("Выйти")}
          onClick={async () => {
            try {
              await api("/auth/logout", "POST");
              qc.clear();
              navigate("/login");
            } catch (e) {
              err(e);
            }
          }}
        >
          <LogOut size={17} />
        </Button>
      </div>
    </aside>
  );
}

function Palette({ open, close }: { open: boolean; close: () => void }) {
  const { boards, openTask, create } = useApp(),
    navigate = useNavigate(),
    [text, setText] = useState("");
  const q = useQuery({
    queryKey: ["search", text],
    queryFn: () =>
      api<TaskList>("/tasks?q=" + encodeURIComponent(text) + "&limit=15"),
    enabled: open && text.length > 0,
  });
  const [index, setIndex] = useState(0);
  type PaletteItem = {
    id: string;
    group: string;
    title: ReactNode;
    icon: ReactNode;
    hint?: string;
    run: () => void;
  };
  const needle = text.trim().toLowerCase();
  const found: PaletteItem[] = (q.data?.items || []).map((t) => ({
    id: t.id,
    group: tr("Задачи"),
    icon: <Circle size={16} aria-hidden="true" />,
    title: t.title,
    run: () => openTask(t.id),
  }));
  const actions: PaletteItem[] = [
    {
      id: "new",
      group: tr("Действия"),
      icon: <Plus size={16} aria-hidden="true" />,
      title: text.trim() ? (
        <>
          {tr("Создать задачу")} «{text.trim()}»
        </>
      ) : (
        tr("Создать задачу")
      ),
      hint: text.trim() ? "Enter" : "C",
      run: () => create(text),
    },
  ];
  const go: PaletteItem[] = [
    {
      id: "go-mine",
      group: tr("Перейти"),
      icon: <Inbox size={16} aria-hidden="true" />,
      title: tr("Мои задачи"),
      run: () => navigate("/"),
    },
    {
      id: "go-delegated",
      group: tr("Перейти"),
      icon: <Send size={16} aria-hidden="true" />,
      title: tr("Я поставил"),
      run: () => navigate("/delegated"),
    },
    ...boards
      .filter((b) => !b.is_archived)
      .map((b) => ({
        id: b.id,
        group: tr("Перейти"),
        icon: <Columns3 size={16} aria-hidden="true" />,
        title: tr("Доска · ") + b.name,
        run: () => navigate("/boards/" + b.id),
      })),
  ].filter((i) => !needle || String(i.title).toLowerCase().includes(needle));
  const items = needle ? [...found, ...actions, ...go] : [...actions, ...go];
  useEffect(() => setIndex(0), [text]);
  const groups = Array.from(new Set(items.map((i) => i.group)));
  return (
    <Modal
      open={open}
      title={tr("Поиск и команды")}
      onClose={close}
      className="palette-dialog"
    >
      <div className="palette-search">
        <Search size={18} aria-hidden="true" />
        <label className="sr-only" htmlFor="palette-input">
          {tr("Найти задачу или команду")}
        </label>
        <input
          id="palette-input"
          autoFocus
          autoComplete="off"
          placeholder={tr("Найти задачу или выполнить действие…")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setIndex((i) => Math.min(i + 1, items.length - 1));
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setIndex((i) => Math.max(i - 1, 0));
            }
            if (e.key === "Enter") {
              e.preventDefault();
              items[index]?.run();
              close();
            }
          }}
        />
        <kbd>Esc</kbd>
      </div>
      <div
        className="palette-list"
        role="listbox"
        aria-label={tr("Результаты поиска")}
        tabIndex={0}
      >
        {groups.map((g) => (
          <div className="palette-group" key={g} role="presentation">
            <h4 role="presentation">{g}</h4>
            {items
              .filter((i) => i.group === g)
              .map((item) => {
                const i = items.indexOf(item);
                return (
                  <button
                    role="option"
                    aria-selected={index === i}
                    className={index === i ? "selected" : ""}
                    key={item.id}
                    onMouseMove={() => index !== i && setIndex(i)}
                    onClick={() => {
                      item.run();
                      close();
                    }}
                  >
                    {item.icon}
                    <span>{item.title}</span>
                    {item.hint && <kbd>{item.hint}</kbd>}
                  </button>
                );
              })}
          </div>
        ))}
        {needle && q.isFetched && !found.length && (
          <p className="palette-empty">{tr("Среди задач ничего не найдено")}</p>
        )}
      </div>
      <div className="palette-foot">
        <span>
          <kbd>↑</kbd>
          <kbd>↓</kbd> {tr("выбрать")}
        </span>
        <span>
          <kbd>Enter</kbd> {tr("открыть")}
        </span>
        <span>
          <kbd>Esc</kbd> {tr("закрыть")}
        </span>
      </div>
      {q.error && (
        <p className="form-error">{tr("Не удалось выполнить поиск")}</p>
      )}
    </Modal>
  );
}

function NotificationBell({ open }: { open: () => void }) {
  const q = useQuery({
    queryKey: ["notifications", "count"],
    queryFn: () => api<{ unread: number }>("/notifications/count"),
  });
  const count = q.data?.unread || 0;
  return (
    <Button
      className="icon notification-bell"
      onClick={open}
      aria-label={tr("Уведомления")}
      aria-description={count ? `${tr("Не прочитано")}: ${count}` : undefined}
    >
      <Bell size={19} />
      {count > 0 && (
        <span className="notification-badge" aria-hidden="true"></span>
      )}
    </Button>
  );
}

function Notifications({ open, close }: { open: boolean; close: () => void }) {
  const { openTask, refresh } = useApp();
  const q = useInfiniteQuery({
    queryKey: ["notifications", "history"],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api<Notification[]>("/notifications?limit=50&offset=" + pageParam),
    getNextPageParam: (last, pages) =>
      last.length === 50 ? pages.flat().length : undefined,
    enabled: open,
  });
  const notifications = q.data?.pages.flat() || [];
  return (
    <Modal
      open={open}
      onClose={close}
      title={tr("Уведомления")}
      className="notifications-dialog"
      focusContainer
    >
      <div className="notifications-head">
        <h2>{tr("Уведомления")}</h2>
        <Button
          className="ghost small"
          onClick={async () => {
            try {
              await api("/notifications/read", "POST");
              refresh();
            } catch (e) {
              err(e);
            }
          }}
        >
          {tr("Прочитать все")}
        </Button>
      </div>
      {q.isError ? (
        <ErrorState error={q.error} retry={() => q.refetch()} />
      ) : q.isPending ? (
        <Loading />
      ) : notifications.length ? (
        <div className="notifications-list">
          {notifications.map((n) => {
            const [head, ...rest] = n.text.split(": ");
            const Icon =
              (
                {
                  comment: MessageSquare,
                  mention: MessageSquare,
                  review: Eye,
                  submit: Eye,
                  review_reminder: Eye,
                  accept: CheckCircle,
                  start: CheckCircle,
                  return: Info,
                  reopen: Info,
                  changed: Info,
                  reminder: Clock,
                  unassigned: UserRound,
                  due: Clock,
                  overdue: AlertCircle,
                  assigned: UserRound,
                  changes: Info,
                } as Record<string, typeof Bell>
              )[n.type] || Bell;
            return (
              <button
                className={"notification " + (!n.is_read ? "unread" : "")}
                key={n.id}
                onClick={() => {
                  if (n.task_id && n.type !== "unassigned") {
                    openTask(n.task_id);
                    close();
                  }
                }}
              >
                <span className="notification-icon" aria-hidden="true">
                  <Icon size={16} />
                </span>
                <span className="notification-text">
                  <strong>{rest.length ? head : n.text}</strong>
                  {rest.length > 0 && <span>{rest.join(": ")}</span>}
                  <small>{notificationTime(n.created_at)}</small>
                </span>
                {!n.is_read && (
                  <span
                    className="notification-dot"
                    role="img"
                    aria-label={tr("Не прочитано")}
                  />
                )}
              </button>
            );
          })}
          {q.hasNextPage && (
            <Button
              disabled={q.isFetchingNextPage}
              onClick={() => q.fetchNextPage()}
            >
              {tr("Показать ещё")}
            </Button>
          )}
        </div>
      ) : (
        <p className="muted notifications-empty">
          {tr("Новых уведомлений нет")}
        </p>
      )}
      <Link className="notification-settings" to="/profile" onClick={close}>
        <SlidersHorizontal size={16} />
        {tr("Настройки уведомлений")}
      </Link>
    </Modal>
  );
}
